import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Package, Theater, Shirt, Plus, ScanLine, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACCENT_STYLES = {
  blue: {
    icon: 'bg-blue-500/10 text-blue-500 dark:text-blue-400',
    border: 'hover:border-blue-500/30',
    gradient: 'from-blue-500/5 to-transparent'
  },
  purple: {
    icon: 'bg-purple-500/10 text-purple-500 dark:text-purple-400',
    border: 'hover:border-purple-500/30',
    gradient: 'from-purple-500/5 to-transparent'
  },
  pink: {
    icon: 'bg-pink-500/10 text-pink-500 dark:text-pink-400',
    border: 'hover:border-pink-500/30',
    gradient: 'from-pink-500/5 to-transparent'
  }
};

const Dashboard = () => {
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    items: 0, categories: 0, locations: 0, users: 0,
    checked_out: 0, available: 0,
    props: 0, containers: 0, prop_categories: 0,
    costumes: 0, wardrobes: 0, costume_categories: 0
  });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, propsRes, containersRes, propCatsRes, costumesRes, wardrobesRes, costumeCatsRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/props`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/containers`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/prop-categories`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/costumes`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/wardrobes`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/costume-categories`, { headers: getAuthHeaders() })
      ]);
      
      setStats({
        items: dashRes.data.total_items,
        categories: dashRes.data.total_categories,
        locations: dashRes.data.total_locations,
        users: dashRes.data.total_users,
        checked_out: dashRes.data.checked_out_items,
        available: dashRes.data.available_items,
        props: propsRes.data.length,
        containers: containersRes.data.length,
        prop_categories: propCatsRes.data.length,
        costumes: costumesRes.data.length,
        wardrobes: wardrobesRes.data.length,
        costume_categories: costumeCatsRes.data.length
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const totalItems = stats.items + stats.props + stats.costumes;

  const avSection = ACCENT_STYLES.blue;
  const propsSection = ACCENT_STYLES.purple;
  const costumesSection = ACCENT_STYLES.pink;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 p-8 border border-slate-800">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-purple-500 rounded-full blur-3xl"></div>
        </div>
        <div className="relative">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white">
            Welcome back, {user?.name?.split(' ')[0] || 'Admin'}
          </h1>
          <p className="text-slate-300 mt-2">Manage your school's AV equipment, props, and costumes all in one place</p>
          
          <div className="mt-6 flex flex-wrap items-center gap-4 text-white">
            <div>
              <div className="text-4xl font-bold">{totalItems}</div>
              <div className="text-sm text-slate-400">Total Assets</div>
            </div>
            <div className="w-px h-12 bg-slate-700"></div>
            <div>
              <div className="text-4xl font-bold text-emerald-400">{stats.available}</div>
              <div className="text-sm text-slate-400">Available</div>
            </div>
            <div className="w-px h-12 bg-slate-700"></div>
            <div>
              <div className="text-4xl font-bold text-amber-400">{stats.checked_out}</div>
              <div className="text-sm text-slate-400">Checked Out</div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Quick Actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <button
            data-testid="quick-add-item"
            onClick={() => navigate('/items')}
            className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 p-4 text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
          >
            <div className="flex flex-col items-start gap-2">
              <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                <Package className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <div className="flex items-center gap-1">
                <span className="font-semibold">Add Item</span>
                <Plus className="w-4 h-4 opacity-70 group-hover:opacity-100" />
              </div>
            </div>
          </button>

          <button
            data-testid="quick-add-prop"
            onClick={() => navigate('/props')}
            className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 p-4 text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
          >
            <div className="flex flex-col items-start gap-2">
              <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                <Theater className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <div className="flex items-center gap-1">
                <span className="font-semibold">Add Prop</span>
                <Plus className="w-4 h-4 opacity-70 group-hover:opacity-100" />
              </div>
            </div>
          </button>

          <button
            data-testid="quick-add-costume"
            onClick={() => navigate('/costumes')}
            className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-pink-500 to-pink-600 p-4 text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
          >
            <div className="flex flex-col items-start gap-2">
              <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                <Shirt className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <div className="flex items-center gap-1">
                <span className="font-semibold">Add Costume</span>
                <Plus className="w-4 h-4 opacity-70 group-hover:opacity-100" />
              </div>
            </div>
          </button>

          <button
            data-testid="quick-scan"
            onClick={() => navigate('/scanner')}
            className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 p-4 text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
          >
            <div className="flex flex-col items-start gap-2">
              <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                <ScanLine className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <div className="flex items-center gap-1">
                <span className="font-semibold">Scan QR</span>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* AV Equipment Section */}
      <Card className={`border-border/50 overflow-hidden transition-colors ${avSection.border}`}>
        <div className={`bg-gradient-to-r ${avSection.gradient} p-6`}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-xl ${avSection.icon}`}>
                <Package className="w-6 h-6" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="text-xl font-bold">AV Equipment</h2>
                <p className="text-sm text-muted-foreground">Audio-visual gear inventory</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/items')} className="gap-1">
              View All
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            <button onClick={() => navigate('/items')} className="text-left p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors group">
              <div className="text-2xl md:text-3xl font-bold group-hover:text-primary transition-colors">{stats.items}</div>
              <div className="text-xs text-muted-foreground mt-1">Total Items</div>
            </button>
            <button onClick={() => navigate('/categories')} className="text-left p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors group">
              <div className="text-2xl md:text-3xl font-bold group-hover:text-primary transition-colors">{stats.categories}</div>
              <div className="text-xs text-muted-foreground mt-1">Categories</div>
            </button>
            <button onClick={() => navigate('/locations')} className="text-left p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors group">
              <div className="text-2xl md:text-3xl font-bold group-hover:text-primary transition-colors">{stats.locations}</div>
              <div className="text-xs text-muted-foreground mt-1">Locations</div>
            </button>
          </div>
        </div>
      </Card>

      {/* Theatre Props Section */}
      <Card className={`border-border/50 overflow-hidden transition-colors ${propsSection.border}`}>
        <div className={`bg-gradient-to-r ${propsSection.gradient} p-6`}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-xl ${propsSection.icon}`}>
                <Theater className="w-6 h-6" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="text-xl font-bold">Theatre Props</h2>
                <p className="text-sm text-muted-foreground">Props inventory in containers</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/props')} className="gap-1">
              View All
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            <button onClick={() => navigate('/props')} className="text-left p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors group">
              <div className="text-2xl md:text-3xl font-bold group-hover:text-primary transition-colors">{stats.props}</div>
              <div className="text-xs text-muted-foreground mt-1">Total Props</div>
            </button>
            <button onClick={() => navigate('/containers')} className="text-left p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors group">
              <div className="text-2xl md:text-3xl font-bold group-hover:text-primary transition-colors">{stats.containers}</div>
              <div className="text-xs text-muted-foreground mt-1">Containers</div>
            </button>
            <button onClick={() => navigate('/prop-categories')} className="text-left p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors group">
              <div className="text-2xl md:text-3xl font-bold group-hover:text-primary transition-colors">{stats.prop_categories}</div>
              <div className="text-xs text-muted-foreground mt-1">Categories</div>
            </button>
          </div>
        </div>
      </Card>

      {/* Costumes Section */}
      <Card className={`border-border/50 overflow-hidden transition-colors ${costumesSection.border}`}>
        <div className={`bg-gradient-to-r ${costumesSection.gradient} p-6`}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-xl ${costumesSection.icon}`}>
                <Shirt className="w-6 h-6" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="text-xl font-bold">Costumes</h2>
                <p className="text-sm text-muted-foreground">Wardrobe and costume inventory</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/costumes')} className="gap-1">
              View All
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            <button onClick={() => navigate('/costumes')} className="text-left p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors group">
              <div className="text-2xl md:text-3xl font-bold group-hover:text-primary transition-colors">{stats.costumes}</div>
              <div className="text-xs text-muted-foreground mt-1">Total Costumes</div>
            </button>
            <button onClick={() => navigate('/wardrobes')} className="text-left p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors group">
              <div className="text-2xl md:text-3xl font-bold group-hover:text-primary transition-colors">{stats.wardrobes}</div>
              <div className="text-xs text-muted-foreground mt-1">Wardrobes</div>
            </button>
            <button onClick={() => navigate('/costume-categories')} className="text-left p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors group">
              <div className="text-2xl md:text-3xl font-bold group-hover:text-primary transition-colors">{stats.costume_categories}</div>
              <div className="text-xs text-muted-foreground mt-1">Categories</div>
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Dashboard;
